import parsePhoneNumber from 'libphonenumber-js';
import React, { useState, useRef, useEffect } from 'react';
import AuthCode, { AuthCodeRef } from 'react-auth-code-input';
import { toast } from 'react-hot-toast';
import axios from 'redaxios';
import { tw } from 'twind';
import { Modal } from './Modal';
import PhoneNumberInput from '../PhoneNumberInput';
import { Button } from '../Button';
import useQueryString from '../../hooks/useQueryString';
import { phoneInputStyle, countryDropdownStyle } from '../ModalShipping';
import { useRudderstack } from 'analytics/RudderStack';
import { useCartStore } from 'store/cart';

interface ModalLoginProps {
  isOpen: boolean;
  requestClose: () => void;
  onSuccess: () => void;
  cartId: string;
  creatorId: string;
  creatorName: string;
  requireAuth: boolean;
}

export const ModalLogin: React.FC<ModalLoginProps> = ({ isOpen, requestClose, onSuccess, cartId, creatorId, creatorName, requireAuth }) => {
  const [session, setSession] = useQueryString();
  const rudder = useRudderstack();

  const onComplete = React.useCallback(() => {
    rudder.track('authentication', {
      cart_id: cartId,
      step: 2,
    });
    onSuccess();
  }, [onSuccess, rudder, cartId]);

  return (
    <Modal open={isOpen} requestClose={requestClose}>
      {session.id && session.phone ? (
        <VerifyPhoneNumber onComplete={onComplete} creatorId={creatorId} cartId={cartId} />
      ) : (
        <ProvidePhoneNumber creatorId={creatorId} creatorName={creatorName} setSessionData={setSession} proceedToCheckout={onComplete} verificationRequired={requireAuth} />
      )}
    </Modal>
  );
};

interface ProvidePhoneNumberProps {
  setSessionData: (session: any) => void;
  proceedToCheckout: () => void;
  verificationRequired: boolean;
  creatorId: string;
  creatorName: string;
}

function ProvidePhoneNumber({ setSessionData, proceedToCheckout, verificationRequired, creatorId, creatorName }: ProvidePhoneNumberProps) {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);

  const { cart, theme, countryCode } = useCartStore();

  const formatted = React.useMemo(() => {
    const PhoneNumberFormatter = parsePhoneNumber(phone);
    return PhoneNumberFormatter?.formatNational();
  }, [phone]);

  const doSubscribe = () => {
    return axios
      .post('/api/subscribe', { creator_id: creatorId, phone_number: phone, cart_id: cart?.id })
      .then(function (response: any) {
        setLoading(false);
        proceedToCheckout();
      })
      .catch(function (err) {
        console.error(err);
        setLoading(false);
      });
  }

  const doLogin = () => {
    return axios
      .post('/api/auth/login', {
        phone_number: phone,
      })
      .then(function (response: any) {
        setLoading(false);
        setSessionData({
          id: response.data.id,
          phone: formatted!,
        });
      })
      .catch(function (err) {
        console.error(err);
        setLoading(false);
      });
  }

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!valid) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);

    let promiseCallback = doSubscribe, onSuccessMessage = 'Phone number verified';
    if (verificationRequired) {
      promiseCallback = doLogin;
      onSuccessMessage = `A verification code has been sent to ${formatted}`;
    }

    const promise = promiseCallback();

    toast.promise(promise, {
      loading: 'Processing...',
      success: onSuccessMessage,
      error: 'Something went wrong',
    });

    await promise;
  };

  return (
    <form onSubmit={onSubmit}>
      <div>
        <h3 className={tw`text-lg leading-6 font-medium text-${theme}-primary`}>Your Phone Number</h3>
        <div className={tw`mt-2`}>
          {verificationRequired ? (
            <p className={tw`text-sm text-${theme}-primary`}>
              Fill out your phone number for Two-Factor Authentication (2FA) to ensure secure checkout.
            </p>
          ): (
            <p className={tw`text-sm text-${theme}-primary`}>Enter your phone number to unlock @{ creatorName }’s CreatorCart and subscribe for access to incredible future offers like this one.</p>
          )}

          <label htmlFor="phone" className={tw`block text-sm font-medium text-${theme}-primary mt-4`}>
            Phone Number:
          </label>
          <PhoneNumberInput
            showFlag
            value={phone}
            onChange={setPhone}
            isValid={setValid}
            styleConfig={phoneInputStyle}
            selectStyleConfig={countryDropdownStyle}
            defaultCountryCode={countryCode}
          />
        </div>
      </div>
      <div className={tw`mt-5 sm:mt-6`}>
        <Button
          className={tw`inline-flex items-center justify-center px-6 py-3 text-sm leading-4 font-bold bg-${theme} text-white relative disabled:cursor-not-allowed focus:outline-none focus:ring-offset-2 focus:ring-white-500 disabled:opacity-60 text-center w-full`}
          type="submit"
          loading={loading}
        >
          Next
        </Button>
      </div>
    </form>
  );
}

interface VerifyPhoneNumberProps {
  onComplete: (success: boolean) => void;
  creatorId: string;
  cartId: string;
}

function VerifyPhoneNumber({ onComplete, creatorId, cartId }: VerifyPhoneNumberProps) {
  const [loading, setLoading] = useState(false);
  const [, setOtp] = useState('');
  const [session] = useQueryString();
  const authInputRef = useRef<AuthCodeRef>(null);

  const { theme } = useCartStore();

  useEffect(() => {
    if (authInputRef.current) {
      authInputRef.current.focus();
    }
  }, [authInputRef]);

  const submitCode = React.useCallback(
    async (code: string) => {
      if (loading) {
        return;
      }
      setLoading(true);
      const promise = axios.post('/api/auth/verify', {
        id: session.id,
        otp: code,
        creator_id: creatorId,
        cart_id: cartId,
      });
      toast.promise(promise, {
        loading: 'Validating...',
        success: 'Success!',
        error: 'Incorrect code, please try again.',
      });

      await promise
        .then((res: any) => {
          onComplete(res.data.success);
        })
        .catch((err) => {
          setLoading(false);
          authInputRef.current?.clear();
        });
    },
    [loading, setLoading, onComplete, authInputRef, session.id, creatorId, cartId]
  );

  const handleOnChange = React.useCallback(
    (code: string) => {
      if (code.length < 6) {
        setOtp(code);
        return;
      }
      submitCode(code);
    },
    [submitCode, setOtp]
  );

  if (loading) {
    return (
      <div className={tw`p-14`}>
        <div className={tw`px-14`}>
          <img src="/img/loading.svg" alt="loading..." />
        </div>
      </div>
    );
  }

  return (
    <form>
      <div>
        <h3 className={tw`text-lg leading-6 font-medium text-${theme}-primary`}>2FA (Two Factor Authentication)</h3>
        <div className={tw`mt-2`}>
          <p className={tw`text-sm text-${theme}-primary`}>
            A verification code has been sent to {session.phone}. Please enter the code to verify your identity and proceed.
          </p>

          <div className={tw`flex justify-center items-center mt-5`}>
            <AuthCode
              ref={authInputRef}
              disabled={loading}
              autoFocus={true}
              ariaLabel="Verification code"
              allowedCharacters="numeric"
              onChange={handleOnChange}
              inputClassName={tw`form-input shadow-sm focus:ring-black block w-10 sm:text-sm border-${theme}-primary border text-center appearance-none`}
              containerClassName={tw`flex flex-1 justify-between items-center max-w-xs`}
            />
          </div>

          <button className={tw`underline text-${theme}-primary text-center w-full mt-5 text-sm`}>Send another code</button>
        </div>
      </div>
    </form>
  );
}
